import React from 'react';
import { Navigate } from 'react-router-dom';

export interface ProtectedRouteProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
  isLoading?: boolean;
  /** Cognito groups the current user belongs to. */
  userGroups?: string[];
  /** If set, user must belong to this group or be redirected to /forbidden. */
  requiredGroup?: string;
  /** Rendered while auth is loading. Defaults to null (no flash). */
  loadingFallback?: React.ReactNode;
}

/**
 * Route guard that checks authentication and optional group membership.
 * Prop-based (no hook) so it can be used across portals with different AuthContexts.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  isAuthenticated,
  isLoading = false,
  userGroups,
  requiredGroup,
  loadingFallback = null,
}) => {
  if (isLoading) {
    return <>{loadingFallback}</>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredGroup && !userGroups?.includes(requiredGroup)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
